import type { InviteMemberResponse, TeamMember } from '@dnx/contracts';
import { STAFF_ROLES } from '@dnx/contracts';
import { Alert, Badge, Button, Card, Field, Input, Select, Spinner } from '@dnx/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, RotateCcw, UserPlus, UserX } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { initials, ROLE_LABELS } from '../lib/labels.js';
import { SectionHeader } from './layout.js';

export const teamQuery = queryOptions({ queryKey: ['team'], queryFn: () => api<TeamMember[]>('/team/members') });

const STATUS: Record<TeamMember['status'], { label: string; tone: 'success' | 'warning' | 'neutral' }> = {
  active: { label: 'Ativo', tone: 'success' },
  invited: { label: 'Convite enviado', tone: 'warning' },
  disabled: { label: 'Desativado', tone: 'neutral' },
};
const when = (iso: string | null) => (iso ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(iso)) : '—');

function InviteForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamMember['roles'][number]>('reception');
  const invite = useMutation({
    mutationFn: () => api<InviteMemberResponse>('/team/members', { method: 'POST', body: { name, email, role } }),
    onSuccess: () => { setName(''); setEmail(''); void qc.invalidateQueries({ queryKey: ['team'] }); },
    // se o e-mail falhar, a pessoa já foi criada: a lista mostra "Convite enviado" e dá para reenviar
    onError: () => void qc.invalidateQueries({ queryKey: ['team'] }),
  });
  const valid = name.trim().length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  return (
    <Card className="mt-6 p-5">
      <p className="font-semibold text-fg">Convidar para a equipe</p>
      <p className="mt-1 text-[13px] text-fg-muted">A pessoa recebe um link por e-mail para criar a senha e cadastrar o app autenticador. O link vale 72 horas.</p>
      <form className="mt-4 grid gap-4 sm:grid-cols-[1fr_1fr_220px_auto] sm:items-end" noValidate
        onSubmit={(e) => { e.preventDefault(); if (valid) invite.mutate(); }}>
        <Field label="Nome"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome completo" autoFocus /></Field>
        <Field label="E-mail"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@clinica.com.br" /></Field>
        <Field label="Papel">
          <Select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
            {STAFF_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </Select>
        </Field>
        <div className="flex gap-2">
          <Button type="submit" loading={invite.isPending} disabled={!valid}><Mail className="size-4" />Enviar convite</Button>
          <Button variant="ghost" onClick={onClose}>Fechar</Button>
        </div>
      </form>
      {invite.data && (
        <Alert tone="success" className="mt-4">
          {invite.data.inviteSent ? `Convite enviado para ${invite.data.member.email}.` : `${invite.data.member.name} já tinha conta e agora faz parte da equipe.`}
        </Alert>
      )}
      {invite.error && <Alert tone="danger" className="mt-4">{invite.error.message}</Alert>}
    </Card>
  );
}

function MemberRow({ m }: { m: TeamMember }) {
  const qc = useQueryClient();
  const [confirmOff, setConfirmOff] = useState(false);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['team'] });
  const role = useMutation({
    mutationFn: (r: string) => api<TeamMember>(`/team/members/${m.userId}`, { method: 'PATCH', body: { role: r } }), onSettled: refresh,
  });
  const resend = useMutation({ mutationFn: () => api<void>(`/team/members/${m.userId}/resend-invite`, { method: 'POST' }) });
  const toggle = useMutation({
    mutationFn: () => api<TeamMember>(`/team/members/${m.userId}/${m.status === 'disabled' ? 'reactivate' : 'deactivate'}`, { method: 'POST' }),
    onSettled: () => { setConfirmOff(false); refresh(); },
  });
  const error = role.error ?? resend.error ?? toggle.error;

  return (
    <li className="px-5 py-4">
      <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-3 md:grid-cols-[auto_minmax(0,1fr)_150px_190px] lg:grid-cols-[auto_minmax(0,1fr)_130px_170px_190px_150px]">
        <span className="grid size-10 shrink-0 place-items-center rounded-full bg-surface-muted text-[12px] font-semibold text-fg-muted ring-1 ring-border">{initials(m.name)}</span>
        <div className="min-w-0">
          <p className="font-medium text-fg">{m.name}{m.isYou && <span className="ml-2 text-[12px] font-normal text-fg-subtle">(você)</span>}</p>
          <p className="truncate text-[13px] text-fg-muted">{m.email}</p>
        </div>
        <span className="col-start-2 md:col-start-auto"><Badge tone={STATUS[m.status].tone}>{STATUS[m.status].label}</Badge></span>
        <span className="hidden text-[12.5px] leading-snug text-fg-muted lg:block">
          {m.status === 'invited' ? 'Ainda não criou a senha' : m.mfaEnrolled ? `Último acesso ${when(m.lastLoginAt)}` : 'Sem autenticador'}
        </span>
        <div className="col-start-2 md:col-start-auto">
          <Select aria-label={`Papel de ${m.name}`} value={m.roles[0]} disabled={role.isPending || m.status === 'disabled'}
            onChange={(e) => role.mutate(e.target.value)}>
            {STAFF_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </Select>
        </div>
        <div className="col-start-2 flex justify-start gap-1 md:col-span-4 md:col-start-2 lg:col-span-1 lg:col-start-auto lg:justify-end">
          {m.status === 'invited' && (
            <Button size="sm" variant="ghost" loading={resend.isPending} onClick={() => resend.mutate()} title="Reenviar convite">
              <RotateCcw className="size-4" />{resend.isSuccess ? 'Reenviado' : 'Reenviar'}
            </Button>
          )}
          {!m.isYou && (m.status === 'disabled'
            ? <Button size="sm" variant="ghost" loading={toggle.isPending} onClick={() => toggle.mutate()}>Reativar</Button>
            : <Button size="sm" variant="ghost" className="text-danger" onClick={() => setConfirmOff(true)}><UserX className="size-4" />Desativar</Button>)}
        </div>
      </div>
      {confirmOff && (
        <Alert tone="warning" title={`Desativar o acesso de ${m.name}?`} className="mt-3">
          <p>A pessoa sai de todos os aparelhos e não entra mais nesta clínica. O histórico dela continua registrado.</p>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="danger" loading={toggle.isPending} onClick={() => toggle.mutate()}>Desativar</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmOff(false)}>Manter</Button>
          </div>
        </Alert>
      )}
      {error && <Alert tone="danger" className="mt-3">{error.message}</Alert>}
    </li>
  );
}

/** Configurações da clínica → Equipe. */
export function TeamSettingsPage() {
  const { data, isLoading } = useQuery(teamQuery);
  const [inviting, setInviting] = useState(false);
  const active = data?.filter((m) => m.status === 'active').length ?? 0;
  const invited = data?.filter((m) => m.status === 'invited').length ?? 0;
  return (
    <div>
      <SectionHeader title="Equipe"
        description={data ? `${active} ${active === 1 ? 'pessoa ativa' : 'pessoas ativas'}${invited ? ` · ${invited} com convite pendente` : ''}. Cada pessoa entra com a própria senha e app autenticador.` : undefined}
        actions={!inviting && <Button onClick={() => setInviting(true)}><UserPlus className="size-4" />Convidar pessoa</Button>} />
      {inviting && <InviteForm onClose={() => setInviting(false)} />}
      {isLoading || !data ? <div className="grid h-40 place-items-center"><Spinner className="size-5 text-fg-subtle" label="Carregando" /></div> : (
        <Card className="mt-6 overflow-hidden">
          <ul className="divide-y divide-border">{data.map((m) => <MemberRow key={m.userId} m={m} />)}</ul>
        </Card>
      )}
    </div>
  );
}
